"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { gsap } from "gsap"
import LoginForm from "./LoginForm"
import RegisterForm from "./RegisterForm"
import HomeNavbar from "./HomeNavbar"
import Footer from "./Footer"

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  const formRef = useRef(null)
  const sidePanelRef = useRef(null)

  useEffect(() => {
    const token = localStorage.getItem("access_token")
    if (token) {
      navigate("/dashboard", { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    setIsLogin(location.pathname === "/auth/login")
  }, [location])

  useEffect(() => {
    // Form animation
    const formElement = formRef.current
    const formContent = formElement?.children[0] // Target the LoginForm or RegisterForm div
    gsap.set(formContent, { opacity: 0, x: isLogin ? -50 : 50 })

    const formObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(formContent, {
              opacity: 1,
              x: 0,
              duration: 0.8,
              ease: "power3.out",
            })
            formObserver.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.3 }
    )

    if (formElement) formObserver.observe(formElement)

    // Side panel animation
    const sidePanelElement = sidePanelRef.current
    const sidePanelChildren = sidePanelElement?.children || []
    gsap.set(sidePanelChildren, { opacity: 0, x: isLogin ? 50 : -50 })

    const sidePanelObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(sidePanelChildren, {
              opacity: 1,
              x: 0,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            })
            sidePanelObserver.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.3 }
    )

    if (sidePanelElement) sidePanelObserver.observe(sidePanelElement)

    return () => {
      if (formElement) formObserver.unobserve(formElement)
      if (sidePanelElement) sidePanelObserver.unobserve(sidePanelElement)
    }
  }, [isLogin])

  const handleLoginSuccess = () => {
    navigate("/dashboard", { replace: true })
  }

  const toggleForm = () => {
    navigate(isLogin ? "/auth/register" : "/auth/login")
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      <HomeNavbar />
      <div className="flex-grow flex items-center justify-center py-8 md:py-16 px-4">
        <div className="bg-slate-700 backdrop-blur-sm rounded-xl w-full max-w-md md:max-w-5xl my-4 md:my-8 flex flex-col md:flex-row overflow-hidden">
          {/* Form Section */}
          <div ref={formRef} className="w-full md:w-1/2 p-6 md:p-8">
            {isLogin ? <LoginForm onSuccess={handleLoginSuccess} /> : <RegisterForm />}
          </div>
          {/* Side Panel */}
          <div
            ref={sidePanelRef}
            className="bg-blue-600 p-8 md:p-12 text-white flex flex-col justify-center items-center w-full md:w-1/2"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-center">
              {isLogin ? "Need an Account?" : "Already Registered?"}
            </h2>
            <p className="mb-6 md:mb-10 text-center text-base md:text-lg text-slate-200">
              {isLogin
                ? "Create an account to start trading with us today!"
                : "Log in to access your trading dashboard now!"}
            </p>
            <button
              className="bg-transparent border-2 border-white text-white py-2 md:py-3 px-6 md:px-10 rounded-lg text-base md:text-lg font-semibold transition-all duration-300 hover:bg-white hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
              onClick={toggleForm}
            >
              {isLogin ? "Sign Up" : "Sign In"}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default AuthPage